import { useCallback, useEffect, useState } from 'react';
import { PermissionState } from '@capacitor/core/types/definitions';
import { Geolocation } from '@capacitor/geolocation';
import { useJsApiLoader } from '@react-google-maps/api';

import { useConfiguration } from '@ocx/data-configuration';

import { Geocoder, type GeoRegion } from './Geocoder';

const GOOGLE_MAPS_SCRIPT_ID = 'ocx-google-maps-script';

const POSITION_OPTIONS = {
  timeout: 20000,
  maximumAge: 500,
};

type GeoRegionCache = {
  key: string;
  region: GeoRegion;
};

let geoRegionCache: GeoRegionCache | null = null;

const toCacheKey = (lat: number, lng: number) => `${lat.toFixed(2)},${lng.toFixed(2)}`; // fixed(2) ~ 1km

const isGranted = (location: PermissionState, coarseLocation: PermissionState) =>
  location === 'granted' || coarseLocation === 'granted';

const fetchGeoRegion = async (): Promise<GeoRegion | null> => {
  const permissions = await Geolocation.checkPermissions();
  if (!isGranted(permissions.location, permissions.coarseLocation)) {
    return null;
  }

  const position = await Geolocation.getCurrentPosition(POSITION_OPTIONS);
  const { latitude: lat, longitude: lng } = position.coords;
  const cacheKey = toCacheKey(lat, lng);

  if (geoRegionCache?.key === cacheKey) {
    return geoRegionCache.region;
  }

  const region = await Geocoder.getGeoRegion({ lat, lng });
  if (!region) {
    return null;
  }

  geoRegionCache = { key: cacheKey, region };
  return region;
};

export type UseCurrentGeoRegionParams = {
  skip?: boolean;
  /** Re-resolve GPS when this changes. Ignored while skip is true. */
  refreshKey?: string;
};

export const useCurrentGeoRegion = (params: UseCurrentGeoRegionParams = {}) => {
  const { skip, refreshKey } = params;
  const googleMapsApiKey = useConfiguration('storeLocator.googleMapsApiKey');

  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey,
  });

  const [geoRegion, setGeoRegion] = useState<GeoRegion | null>(null);
  const [resolvedKey, setResolvedKey] = useState<string | undefined>();

  const isResolving = !skip && !loadError && (!isLoaded || resolvedKey !== refreshKey);

  const applyRegion = useCallback(
    (region: GeoRegion | null) => {
      setGeoRegion(region);
      setResolvedKey(refreshKey);
    },
    [refreshKey],
  );

  useEffect(() => {
    if (skip) {
      setGeoRegion(null);
      setResolvedKey(undefined);
      return undefined;
    }

    if (loadError) {
      setGeoRegion(null);
      return undefined;
    }

    if (!isLoaded) {
      return undefined;
    }

    let cancelled = false;

    fetchGeoRegion()
      .then((region) => {
        if (!cancelled) {
          applyRegion(region);
        }
      })
      .catch(() => {
        if (!cancelled) {
          applyRegion(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [skip, isLoaded, loadError, applyRegion]);

  const refresh = useCallback(async () => {
    if (skip || !isLoaded || loadError) {
      return null;
    }

    try {
      const region = await fetchGeoRegion();
      applyRegion(region);
      return region;
    } catch {
      applyRegion(null);
      return null;
    }
  }, [skip, isLoaded, loadError, applyRegion]);

  return { geoRegion, isResolving, refresh };
};
